import {
  fraseDeDigitando,
  podeGerirGrupo,
  carimboDaMensagem,
  mesmoDia,
  rotuloDoDia,
  TAMANHO_MAXIMO_ANEXO,
  TIPOS_ANEXO_ACEITOS,
  type CanalDetalhe,
  type CanalResumo,
  type ContatoInterno,
  type ResultadoBuscaInterna,
  type MensagemCanalChat,
  type ParticipanteCanal,
} from '@mymaxx/shared';
import {
  Anexo,
  arquivoDeAudio,
  Aviso,
  BarraGravacao,
  Botao,
  BotaoMicrofone,
  Citacao,
  useGravadorAudio,
  useRolagemDoFim,
} from '@mymaxx/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  BellOff,
  CheckCheck,
  Info,
  MoreVertical,
  Paperclip,
  Pin,
  Pencil,
  Phone,
  Plus,
  Search,
  Send,
  Star,
  Users,
  X,
} from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { NovaConversa } from './NovaConversa';
import { PerfilDaConversa } from './PerfilDaConversa';
import { Link, useSearchParams } from 'react-router';
import { ErroApi } from '../lib/api';
import { useDiscar } from '../lib/discar';
import { internoApi } from '../lib/interno-api';
import { ativarAvisoInterno } from '../lib/push';
import { useSessao } from '../lib/sessao';
import { useAvisarQueDigito, useEventosInternos, useQuemDigita } from '../lib/interno-tempo-real';

/**
 * As conversas internas no app do técnico (D20).
 *
 * É a metade de campo do que no painel é a aba Conversas: o técnico lê e responde o que a
 * equipe falou com ele, em privado ou em grupo, e manda foto do que está vendo. Nasceu
 * para acabar com o Telegram — era lá que isso acontecia, fora do alcance da empresa.
 *
 * Uma coluna só, e não duas como no painel: isto é celular em pé, no meio da rua. A lista
 * dá lugar ao fio quando ele abre uma conversa, como em qualquer mensageiro.
 *
 * O que ele NÃO faz aqui: começar conversa com alguém. A regra é do servidor, não desta
 * tela — a demanda que o técnico levanta tem caminho próprio e auditável, que é abrir
 * atendimento no setor (Liberação, Rotas). Sem isso, o canal interno viraria uma segunda
 * fila invisível, sem protocolo e sem ninguém responsável.
 */
export function Conversas() {
  const qc = useQueryClient();
  const [parametros, definirParametros] = useSearchParams();
  const abertoId = parametros.get('canal');

  const canais = useQuery({ queryKey: ['interno-canais'], queryFn: internoApi.canais });
  const canal = useQuery({
    queryKey: ['interno-canal', abertoId],
    queryFn: () => internoApi.detalhe(abertoId!),
    enabled: !!abertoId,
  });

  const abrir = (id: string | null) =>
    definirParametros(id ? { canal: id } : {}, { replace: true });
  const recarregarLista = () => void qc.invalidateQueries({ queryKey: ['interno-canais'] });

  // O aviso no celular é pedido aqui, e não na abertura do app: quem entra nesta tela está
  // dizendo que quer ser chamado por ela. Pedir permissão antes disso é o caminho mais
  // curto para a pessoa negar para sempre.
  useEffect(() => {
    void ativarAvisoInterno();
  }, []);

  const trocarMensagem = (canalId: string, mensagem: MensagemCanalChat, acrescentar: boolean) => {
    if (canalId !== abertoId) return;
    qc.setQueryData<CanalDetalhe>(['interno-canal', abertoId], (atual) => {
      if (!atual) return atual;
      const existe = atual.mensagens.some((m) => m.id === mensagem.id);
      if (existe) {
        return {
          ...atual,
          mensagens: atual.mensagens.map((m) => (m.id === mensagem.id ? mensagem : m)),
        };
      }
      return acrescentar ? { ...atual, mensagens: [...atual.mensagens, mensagem] } : atual;
    });
  };

  useEventosInternos({
    aoReceberMensagem: (e) => {
      trocarMensagem(e.canalId, e.mensagem, true);
      if (e.canalId === abertoId) {
        void internoApi.marcarLido(e.canalId).then(recarregarLista);
      }
      recarregarLista();
    },
    aoMudarMensagem: (e) => trocarMensagem(e.canalId, e.mensagem, false),
    aoMudarCanal: () => {
      recarregarLista();
      if (abertoId) void qc.invalidateQueries({ queryKey: ['interno-canal', abertoId] });
    },
    aoLer: (e) => {
      if (e.canalId !== abertoId) return;
      qc.setQueryData<CanalDetalhe>(['interno-canal', abertoId], (atual) =>
        atual
          ? {
              ...atual,
              membros: atual.membros.map((m) =>
                m.tipo === e.quem.tipo && m.id === e.quem.id ? { ...m, lidoAte: e.lidoAte } : m,
              ),
            }
          : atual,
      );
    },
    aoMudarPresenca: (e) => {
      // A bolinha acende e apaga sem recarregar: vale na lista e no fio aberto.
      qc.setQueryData<CanalResumo[]>(['interno-canais'], (atual) =>
        atual?.map((c) =>
          c.comQuem && c.comQuem.tipo === e.quem.tipo && c.comQuem.id === e.quem.id
            ? { ...c, comQuem: { ...c.comQuem, online: e.online, vistoEm: e.vistoEm } }
            : c,
        ),
      );
      if (!abertoId) return;
      qc.setQueryData<CanalDetalhe>(['interno-canal', abertoId], (atual) =>
        atual
          ? {
              ...atual,
              comQuem:
                atual.comQuem &&
                atual.comQuem.tipo === e.quem.tipo &&
                atual.comQuem.id === e.quem.id
                  ? { ...atual.comQuem, online: e.online, vistoEm: e.vistoEm }
                  : atual.comQuem,
              membros: atual.membros.map((m) =>
                m.tipo === e.quem.tipo && m.id === e.quem.id
                  ? { ...m, online: e.online, vistoEm: e.vistoEm }
                  : m,
              ),
            }
          : atual,
      );
    },
  });

  // Abrir é ler: o contador zera assim que a conversa aparece na tela.
  useEffect(() => {
    if (!abertoId) return;
    void internoApi
      .marcarLido(abertoId)
      .then(() => qc.invalidateQueries({ queryKey: ['interno-canais'] }));
  }, [abertoId, qc]);

  // Os mesmos filtros do painel, na mesma ordem: quem usa os dois não reaprende nada.
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [vendoArquivadas, setVendoArquivadas] = useState(false);
  const [comecando, setComecando] = useState(false);
  const [vendoPerfil, setVendoPerfil] = useState(false);
  // Trocar de conversa fecha o perfil: sem isto, abrir a próxima mostrava os dados dela
  // em vez do fio, porque a tela lembrava a escolha anterior.
  useEffect(() => setVendoPerfil(false), [abertoId]);
  const [menuDoTopo, setMenuDoTopo] = useState(false);

  // A busca é segurada: procurar a cada tecla manda uma consulta por letra digitada.
  const [procurado, setProcurado] = useState('');
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setTermo(procurado.trim()), 300);
    return () => clearTimeout(id);
  }, [procurado]);
  const busca = useQuery({
    queryKey: ['interno-busca', termo],
    queryFn: () => internoApi.buscar(termo),
    enabled: termo.length >= 2,
  });

  if (comecando) {
    return (
      <NovaConversa
        onFechar={() => setComecando(false)}
        onAberta={(id) => {
          setComecando(false);
          recarregarLista();
          abrir(id);
        }}
      />
    );
  }

  if (abertoId && canal.data) {
    return vendoPerfil ? (
      <PerfilDaConversa canal={canal.data} onVoltar={() => setVendoPerfil(false)} />
    ) : (
      <Fio
        canal={canal.data}
        onVoltar={() => abrir(null)}
        onVerPerfil={() => setVendoPerfil(true)}
      />
    );
  }

  const todas = canais.data ?? [];
  const arquivadas = todas.filter((c) => c.arquivado);
  const lista = vendoArquivadas
    ? arquivadas
    : todas.filter((c) => !c.arquivado && cabeNoFiltro(c, filtro));

  return (
    <main className="flex min-h-dvh flex-col bg-neutral-50 dark:bg-neutral-950">
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <Link to="/conta" aria-label="Voltar" className="text-neutral-500">
          <ArrowLeft size={20} />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">Conversas</p>
          <p className="truncate text-xs text-neutral-500">A equipe da MAXX falando com você</p>
        </div>

        {/* O menu do topo é o lugar do que não é do dia a dia: criar grupo e ver o
            arquivo. Fica longe do polegar de propósito — o que se faz o tempo todo é
            abrir conversa, e isso é a lista inteira. */}
        <div className="relative shrink-0">
          <button
            type="button"
            aria-label="Mais opções"
            onClick={() => setMenuDoTopo((v) => !v)}
            className="p-1 text-neutral-500"
          >
            <MoreVertical size={20} />
          </button>
          {menuDoTopo && (
            <div className="absolute right-0 z-30 mt-1 w-52 rounded-lg border border-neutral-200 bg-white p-1 text-sm shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
              <AcaoDaLinha
                onClick={() => {
                  setMenuDoTopo(false);
                  setComecando(true);
                }}
              >
                <Users size={14} /> Novo grupo
              </AcaoDaLinha>
              <AcaoDaLinha
                onClick={() => {
                  setMenuDoTopo(false);
                  setVendoArquivadas((v) => !v);
                }}
              >
                <Archive size={14} />
                {vendoArquivadas ? 'Voltar às conversas' : 'Arquivadas'}
              </AcaoDaLinha>
            </div>
          )}
        </div>
      </header>

      <div className="border-b border-neutral-200 bg-white px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
        <label className="flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 dark:bg-neutral-800">
          <Search size={16} className="shrink-0 text-neutral-400" />
          <input
            value={procurado}
            onChange={(e) => setProcurado(e.target.value)}
            placeholder="Procurar mensagem, grupo ou pessoa"
            aria-label="Procurar mensagem, grupo ou pessoa"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </label>
      </div>

      {/* Os filtros rolam na horizontal: no celular em pé não cabem quatro pastilhas e
        quebrar linha comeria a altura que a lista precisa. */}
      {!vendoArquivadas && (
        <div className="flex gap-2 overflow-x-auto border-b border-neutral-200 bg-white px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
          {FILTROS.map((op) => (
            <button
              key={op.id}
              type="button"
              onClick={() => setFiltro(op.id)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs transition ${
                filtro === op.id
                  ? 'bg-marca-600 text-white'
                  : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
              }`}
            >
              {op.rotulo}
            </button>
          ))}
        </div>
      )}

      {(arquivadas.length > 0 || vendoArquivadas) && (
        <button
          type="button"
          onClick={() => setVendoArquivadas((v) => !v)}
          className="flex items-center gap-2 border-b border-neutral-200 bg-white px-4 py-2 text-left text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900"
        >
          {vendoArquivadas ? <ArrowLeft size={14} /> : <Archive size={14} />}
          <span className="flex-1">{vendoArquivadas ? 'Voltar às conversas' : 'Arquivadas'}</span>
          {!vendoArquivadas && <span>{arquivadas.length}</span>}
        </button>
      )}

      <div className="flex-1 overflow-y-auto pb-24">
        {termo.length >= 2 && (
          <Achados
            resultado={busca.data}
            carregando={busca.isPending}
            onAbrir={(id) => {
              setProcurado('');
              abrir(id);
            }}
          />
        )}
        {termo.length < 2 && canais.isPending && (
          <p className="p-6 text-center text-sm text-neutral-500">Carregando…</p>
        )}
        {termo.length < 2 && canais.isError && (
          <div className="p-3">
            <Aviso tipo="erro">Não consegui carregar suas conversas.</Aviso>
          </div>
        )}
        {termo.length < 2 && canais.isSuccess && lista.length === 0 && (
          <p className="px-6 py-10 text-center text-sm text-neutral-500">
            {vendoArquivadas
              ? 'Nada arquivado.'
              : filtro === 'todos'
                ? 'Nenhuma conversa ainda. Quando alguém da equipe falar com você, ela aparece aqui.'
                : `Nada em "${FILTROS.find((o) => o.id === filtro)?.rotulo}".`}
          </p>
        )}
        <ul
          className={`divide-y divide-neutral-200 dark:divide-neutral-800 ${
            termo.length >= 2 ? 'hidden' : ''
          }`}
        >
          {lista.map((c) => (
            <li key={c.id}>
              <Linha
                canal={c}
                onAbrir={() => abrir(c.id)}
                onMudou={recarregarLista}
                onVerPerfil={() => {
                  abrir(c.id);
                  setVendoPerfil(true);
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Redondo, azul e no canto de baixo: o polegar alcança sem trocar a mão de
          posição, que é o gesto de quem segura o celular com luva. */}
      <button
        type="button"
        onClick={() => setComecando(true)}
        aria-label="Nova conversa"
        className="bg-marca-600 fixed right-5 bottom-5 flex size-14 items-center justify-center rounded-full text-white shadow-lg transition active:scale-95"
      >
        <Plus size={26} />
      </button>
    </main>
  );
}

/**
 * A barra das mensagens fixadas, acima do fio.
 *
 * Mostra uma de cada vez e alterna no toque: no celular em pé, cinco recados empilhados
 * comeriam a conversa inteira.
 */
function BarraFixadas({
  canal,
  podeSoltar,
  onMudou,
}: {
  canal: CanalDetalhe;
  podeSoltar: boolean;
  onMudou: () => void;
}) {
  const [qual, setQual] = useState(0);
  const fixadas = canal.fixadas ?? [];
  if (fixadas.length === 0) return null;

  const indice = qual % fixadas.length;
  const atual = fixadas[indice]!;

  return (
    <div className="flex items-center gap-2 border-b border-neutral-200 bg-amber-50 px-4 py-2 dark:border-neutral-800 dark:bg-amber-950/30">
      <Pin size={13} className="shrink-0 text-amber-600 dark:text-amber-400" />
      <button
        type="button"
        onClick={() => fixadas.length > 1 && setQual((q) => q + 1)}
        className="min-w-0 flex-1 text-left"
      >
        <span className="block truncate text-[11px] text-neutral-500">
          Fixada{atual.fixada?.porNome ? ` por ${atual.fixada.porNome}` : ''}
        </span>
        <span className="block truncate text-xs">
          {atual.texto?.trim() || (atual.midia ? atual.midia.nomeOriginal : 'Anexo')}
        </span>
      </button>
      {fixadas.length > 1 && (
        <span className="shrink-0 text-[11px] text-neutral-500">
          {indice + 1}/{fixadas.length}
        </span>
      )}
      {podeSoltar && (
        <button
          type="button"
          aria-label="Soltar do alto"
          onClick={() => void internoApi.desafixarMensagem(canal.id, atual.id).then(onMudou)}
          className="shrink-0 p-1 text-neutral-400"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/** A foto do grupo na lista e no cabeçalho; sem foto, o ícone de gente de sempre. */
function FotoDoGrupo({ url }: { url?: string | null }) {
  if (!url) return <Users size={14} className="shrink-0 text-neutral-400" />;
  return <img src={url} alt="" className="size-6 shrink-0 rounded-full object-cover" />;
}

type Filtro = 'todos' | 'grupos' | 'nao_lidos' | 'favoritos';

const FILTROS: { id: Filtro; rotulo: string }[] = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'grupos', rotulo: 'Grupos' },
  { id: 'nao_lidos', rotulo: 'Não lidos' },
  { id: 'favoritos', rotulo: 'Favoritos' },
];

function cabeNoFiltro(canal: CanalResumo, filtro: Filtro): boolean {
  if (filtro === 'grupos') return canal.tipo === 'grupo';
  if (filtro === 'nao_lidos') return canal.naoLidas > 0;
  if (filtro === 'favoritos') return canal.favorito;
  return true;
}

/**
 * O que a busca achou: conversas, pessoas e mensagens, em três blocos.
 *
 * Três blocos e não uma lista só porque as três coisas respondem perguntas diferentes:
 * "onde está aquele grupo", "quem é fulano" e "onde foi que falamos daquilo". Misturadas,
 * a terceira — que é a mais longa — enterra as outras duas.
 */
function Achados({
  resultado,
  carregando,
  onAbrir,
}: {
  resultado: ResultadoBuscaInterna | undefined;
  carregando: boolean;
  onAbrir: (canalId: string) => void;
}) {
  if (carregando) {
    return <p className="p-6 text-center text-sm text-neutral-500">Procurando…</p>;
  }
  const nada =
    !resultado ||
    (resultado.canais.length === 0 &&
      resultado.contatos.length === 0 &&
      resultado.mensagens.length === 0);
  if (nada) {
    return <p className="px-6 py-10 text-center text-sm text-neutral-500">Nada encontrado.</p>;
  }

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
      {resultado.canais.length > 0 && (
        <BlocoDeAchados titulo="Conversas">
          {resultado.canais.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onAbrir(c.id)}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
            >
              <span className="truncate">{c.nome}</span>
            </button>
          ))}
        </BlocoDeAchados>
      )}

      {resultado.contatos.length > 0 && (
        <BlocoDeAchados titulo="Pessoas">
          {resultado.contatos.map((c) => (
            <ContatoAchado key={`${c.tipo}:${c.id}`} contato={c} onAbrir={onAbrir} />
          ))}
        </BlocoDeAchados>
      )}

      {resultado.mensagens.length > 0 && (
        <BlocoDeAchados titulo="Mensagens">
          {resultado.mensagens.map((m) => (
            <button
              key={m.mensagemId}
              type="button"
              onClick={() => onAbrir(m.canalId)}
              className="block w-full px-4 py-2 text-left"
            >
              <p className="truncate text-xs text-neutral-500">
                {m.canalNome}
                {m.autorNome ? ` · ${m.autorNome}` : ''}
              </p>
              <p className="line-clamp-2 text-sm">{m.trecho}</p>
            </button>
          ))}
        </BlocoDeAchados>
      )}
    </div>
  );
}

function BlocoDeAchados({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="bg-white dark:bg-neutral-900">
      <h2 className="px-4 pt-3 pb-1 text-[11px] font-medium text-neutral-500 uppercase">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/** Uma pessoa achada na busca: tocar abre (ou reencontra) a conversa com ela. */
function ContatoAchado({
  contato,
  onAbrir,
}: {
  contato: ContatoInterno;
  onAbrir: (canalId: string) => void;
}) {
  const [abrindo, setAbrindo] = useState(false);
  return (
    <button
      type="button"
      disabled={abrindo}
      onClick={() => {
        setAbrindo(true);
        void internoApi
          .abrirDireto(
            contato.tipo === 'usuario' ? { usuarioId: contato.id } : { clienteId: contato.id },
          )
          .then((canal) => onAbrir(canal.id))
          .finally(() => setAbrindo(false));
      }}
      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm disabled:opacity-60"
    >
      <span className="min-w-0 flex-1 truncate">{contato.nome}</span>
      <span className="shrink-0 text-xs text-neutral-500">
        {contato.tipo === 'tecnico' ? 'Técnico' : 'Equipe'}
      </span>
    </button>
  );
}

function Linha({
  canal,
  onAbrir,
  onMudou,
  onVerPerfil,
}: {
  canal: CanalResumo;
  onAbrir: () => void;
  onMudou: () => void;
  onVerPerfil: () => void;
}) {
  const escrevendo = fraseDeDigitando(useQuemDigita(canal.id));
  /**
   * As duas ações ficam atrás de um toque, e não de um toque longo.
   *
   * Toque longo é o gesto do WhatsApp, mas é o gesto que ninguém descobre sozinho — e aqui
   * quem usa é técnico de campo, de luva, no sol, sem tempo de explorar a tela.
   */
  const [acoes, setAcoes] = useState(false);

  return (
    <div className="flex items-start gap-1 bg-white px-4 py-3 dark:bg-neutral-900">
      <button type="button" onClick={onAbrir} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          {canal.tipo === 'grupo' ? (
            <FotoDoGrupo url={canal.avatarUrl} />
          ) : (
            <Retrato pessoa={canal.comQuem} />
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{canal.nome}</span>
          {canal.favorito && <Star size={12} className="shrink-0 fill-amber-400 text-amber-500" />}
          {canal.naoLidas > 0 && (
            <span className="shrink-0 rounded-full bg-marca-600 px-1.5 text-[11px] font-medium text-white">
              {canal.naoLidas}
            </span>
          )}
        </div>
        <p
          className={`truncate text-xs ${
            escrevendo ? 'text-marca-600 dark:text-marca-300' : 'text-neutral-500'
          }`}
        >
          {escrevendo ?? canal.ultimaMensagem ?? 'Sem mensagens'}
        </p>
      </button>

      <div className="relative shrink-0">
        <button
          type="button"
          aria-label="Opções da conversa"
          onClick={() => setAcoes((v) => !v)}
          className="p-1 text-neutral-400"
        >
          <MoreVertical size={16} />
        </button>
        {acoes && (
          <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-neutral-200 bg-white p-1 text-sm shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
            <AcaoDaLinha
              onClick={() => {
                setAcoes(false);
                void internoApi.favoritarCanal(canal.id, !canal.favorito).then(onMudou);
              }}
            >
              <Star size={14} className={canal.favorito ? 'fill-amber-400 text-amber-500' : ''} />
              {canal.favorito ? 'Tirar dos favoritos' : 'Favoritar'}
            </AcaoDaLinha>
            <AcaoDaLinha
              onClick={() => {
                setAcoes(false);
                void internoApi.arquivar(canal.id, !canal.arquivado).then(onMudou);
              }}
            >
              <Archive size={14} />
              {canal.arquivado ? 'Tirar do arquivo' : 'Arquivar'}
            </AcaoDaLinha>
            <AcaoDaLinha
              onClick={() => {
                setAcoes(false);
                void internoApi.fixar(canal.id, !canal.fixado).then(onMudou);
              }}
            >
              <Pin size={14} className={canal.fixado ? 'fill-current' : ''} />
              {canal.fixado ? 'Soltar do topo' : 'Fixar no topo'}
            </AcaoDaLinha>
            {/* Dois prazos, como no painel: oito horas cobre um turno, e "até eu
                desligar" é para quem vai ficar fora por tempo que não sabe dizer. */}
            <AcaoDaLinha
              onClick={() => {
                setAcoes(false);
                void internoApi.silenciar(canal.id, canal.silenciado ? 0 : 8).then(onMudou);
              }}
            >
              <BellOff size={14} />
              {canal.silenciado ? 'Voltar a avisar' : 'Silenciar por 8 horas'}
            </AcaoDaLinha>
            {!canal.silenciado && (
              <AcaoDaLinha
                onClick={() => {
                  setAcoes(false);
                  void internoApi.silenciar(canal.id, null).then(onMudou);
                }}
              >
                <BellOff size={14} />
                Silenciar até eu desligar
              </AcaoDaLinha>
            )}
            {/* Ver quem é sem precisar entrar na conversa: a queixa de 21/10 era
                justamente ter que abrir o fio para descobrir com quem se fala. */}
            <AcaoDaLinha
              onClick={() => {
                setAcoes(false);
                onVerPerfil();
              }}
            >
              <Info size={14} />
              {canal.tipo === 'grupo' ? 'Dados do grupo' : 'Dados do contato'}
            </AcaoDaLinha>
          </div>
        )}
      </div>
    </div>
  );
}

function AcaoDaLinha({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
    >
      {children}
    </button>
  );
}

function Fio({
  canal,
  onVoltar,
  onVerPerfil,
}: {
  canal: CanalDetalhe;
  onVoltar: () => void;
  onVerPerfil: () => void;
}) {
  const meuId = useSessao((e) => e.cliente?.id);
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** A mensagem que está sendo respondida, se houver. */
  const [respondendo, setRespondendo] = useState<MensagemCanalChat | null>(null);
  /** A mensagem que está sendo corrigida; o texto dela vai para o campo. */
  const [editando, setEditando] = useState<MensagemCanalChat | null>(null);
  const fim = useRef<HTMLDivElement>(null);
  const escolherArquivo = useRef<HTMLInputElement>(null);
  const avisarQueDigito = useAvisarQueDigito();
  /** Quando o último aviso de "estou escrevendo" saiu — o volume é segurado aqui. */
  const ultimoAviso = useRef(0);

  const gravador = useGravadorAudio({
    onErro: setErro,
    onGravado: (audio, mimeType) => void anexar(arquivoDeAudio(audio, mimeType)),
  });

  useRolagemDoFim(fim, `${canal.id}:${canal.mensagens.length}`);

  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['interno-canal', canal.id] });
    void qc.invalidateQueries({ queryKey: ['interno-canais'] });
  };

  async function enviar() {
    if (gravador.gravando) {
      gravador.finalizar();
      return;
    }
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      if (editando) await internoApi.editar(canal.id, editando.id, conteudo);
      else await internoApi.escrever(canal.id, conteudo, respondendo?.id);
      setTexto('');
      setRespondendo(null);
      setEditando(null);
      recarregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar');
    } finally {
      setEnviando(false);
    }
  }

  /**
   * Avisa que estou escrevendo, no máximo uma vez a cada três segundos — o aviso vale
   * quatro do outro lado, então isso o mantém aceso sem virar um pacote por tecla. No 4G
   * do campo, isso importa.
   */
  function aoEscrever(valor: string) {
    setTexto(valor);
    if (!valor.trim() || editando) return;
    if (Date.now() - ultimoAviso.current < 3000) return;
    ultimoAviso.current = Date.now();
    avisarQueDigito(canal.id);
  }

  async function apagar(mensagem: MensagemCanalChat) {
    if (!confirm('Apagar esta mensagem? A equipe vai ver que ela foi apagada.')) return;
    try {
      await internoApi.apagar(canal.id, mensagem.id);
      if (editando?.id === mensagem.id) {
        setEditando(null);
        setTexto('');
      }
      recarregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui apagar');
    }
  }

  /** Foto do que ele está vendo em campo — o que mais circulava nos grupos do Telegram. */
  async function anexar(arquivo: File) {
    if (arquivo.size > TAMANHO_MAXIMO_ANEXO) {
      setErro('Arquivo muito grande (máximo 20 MB).');
      return;
    }
    if (!TIPOS_ANEXO_ACEITOS.includes(arquivo.type)) {
      setErro('Tipo de arquivo não aceito. Envie imagem, vídeo, áudio, PDF ou documento.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await internoApi.enviarArquivo(canal.id, arquivo);
      recarregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar o arquivo');
    } finally {
      setEnviando(false);
      if (escolherArquivo.current) escolherArquivo.current.value = '';
    }
  }

  const presentes = canal.membros.filter((m) => !m.saiu);
  // Vem da moldura: em grupo pode ser mais de uma pessoa escrevendo ao mesmo tempo.
  const escrevendo = fraseDeDigitando(useQuemDigita(canal.id));
  // Na conversa de duas pessoas qualquer um dos dois solta; no grupo, só quem administra —
  // e o técnico de campo quase nunca administra, então aqui a barra costuma ser só leitura.
  const podeFixar = canal.tipo === 'direto' || podeGerirGrupo(canal.meuPapel);
  const ligarPara = useDiscar((e) => e.ligarPara);

  return (
    <main className="flex min-h-dvh flex-col bg-neutral-50 dark:bg-neutral-950">
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <button type="button" aria-label="Voltar" onClick={onVoltar} className="text-neutral-500">
          <ArrowLeft size={20} />
        </button>
        {/* O nome no alto abre o perfil, como no WhatsApp: é onde a mão vai procurar
            "quem é essa pessoa" ou "quem está neste grupo". */}
        <button
          type="button"
          onClick={onVerPerfil}
          className="min-w-0 flex-1 text-left"
          aria-label="Ver dados da conversa"
        >
          <p className="flex items-center gap-2 truncate text-sm font-medium">
            {canal.tipo === 'direto' ? (
              <Retrato pessoa={canal.comQuem} />
            ) : (
              <FotoDoGrupo url={canal.avatarUrl} />
            )}
            {canal.nome}
          </p>
          <p className="truncate text-xs text-neutral-500">
            {escrevendo
              ? escrevendo
              : canal.tipo === 'grupo'
                ? presentes.map((m) => m.nome).join(', ')
                : canal.comQuem
                  ? situacao(canal.comQuem)
                  : ''}
          </p>
        </button>
        {/* Ligar só no 1:1: voz em grupo é outra conversa e não é o que a operação
          precisava primeiro. */}
        {canal.tipo === 'direto' && (
          <button
            type="button"
            aria-label="Ligar por voz"
            disabled={!ligarPara}
            onClick={() => ligarPara?.(canal.id, canal.nome)}
            className="text-neutral-500 disabled:opacity-40"
          >
            <Phone size={20} />
          </button>
        )}
      </header>

      <BarraFixadas canal={canal} podeSoltar={podeFixar} onMudou={recarregar} />

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-4">
        {canal.mensagens.length === 0 && (
          <p className="py-8 text-center text-sm text-neutral-500">Nada por aqui ainda.</p>
        )}
        {canal.mensagens.map((m, i) => {
          const minha = m.autor?.tipo === 'tecnico' && m.autor.id === meuId;
          // A faixa entra quando o dia muda em relação à anterior — e antes da
          // primeira, que sempre abre um dia.
          const anterior = canal.mensagens[i - 1];
          const abreODia = !anterior || !mesmoDia(anterior.criadaEm, m.criadaEm);
          return (
            <Fragment key={m.id}>
              {abreODia && <FaixaDoDia quando={m.criadaEm} />}
              <Bolha
                key={m.id}
                mensagem={m}
                grupo={canal.tipo === 'grupo'}
                minha={minha}
                lida={minha && lidaPorTodos(m, canal.membros, meuId)}
                onResponder={() => {
                  setEditando(null);
                  setRespondendo(m);
                }}
                onEditar={
                  minha && m.tipo === 'texto'
                    ? () => {
                        setRespondendo(null);
                        setEditando(m);
                        setTexto(m.texto ?? '');
                      }
                    : undefined
                }
                onApagar={minha ? () => void apagar(m) : undefined}
              />
            </Fragment>
          );
        })}
        <div ref={fim} />
      </div>

      {erro && (
        <div className="px-3 pb-2">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
        className="flex flex-col gap-2 border-t border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900"
      >
        {respondendo && (
          <Citacao
            autor={respondendo.autor?.nome ?? 'Alguém'}
            resumo={respondendo.texto?.trim() || 'Anexo'}
            onCancelar={() => setRespondendo(null)}
            className="bg-black/5 dark:bg-white/10"
          />
        )}
        {editando && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
            <Pencil size={13} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">Corrigindo uma mensagem enviada</span>
            <button
              type="button"
              onClick={() => {
                setEditando(null);
                setTexto('');
              }}
              className="shrink-0 underline"
            >
              cancelar
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={escolherArquivo}
            type="file"
            className="hidden"
            accept={TIPOS_ANEXO_ACEITOS.join(',')}
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo) void anexar(arquivo);
            }}
          />
          {gravador.gravando ? (
            <BarraGravacao gravador={gravador} />
          ) : (
            <>
              <button
                type="button"
                aria-label="Anexar arquivo"
                onClick={() => escolherArquivo.current?.click()}
                className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <Paperclip size={20} />
              </button>
              <BotaoMicrofone gravador={gravador} desabilitado={enviando} tamanho={20} />
              <textarea
                value={texto}
                rows={1}
                placeholder="Escreva para a equipe…"
                aria-label="Mensagem"
                onChange={(e) => aoEscrever(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void enviar();
                  }
                }}
                className="max-h-32 min-h-10 flex-1 resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-950"
              />
            </>
          )}
          <Botao
            type="submit"
            carregando={enviando}
            aria-label={gravador.gravando ? 'Enviar áudio' : 'Enviar'}
          >
            <Send size={18} />
          </Botao>
        </div>
      </form>
    </main>
  );
}

/**
 * Uma mensagem no fio interno.
 *
 * Quem decide o lado da bolha é esta tela, comparando o autor com quem está logado — e não
 * o servidor. A mensagem empurrada pelo WebSocket é montada uma vez só, com a identidade de
 * quem escreveu: um "minha" vindo pronto chegaria marcado como próprio para todo mundo.
 */
/**
 * A faixa que separa os dias dentro da conversa.
 *
 * Grudada no topo enquanto o dia rola, como no WhatsApp: quem desce trinta mensagens
 * continua vendo de que dia são, sem ter que subir de volta para descobrir.
 */
function FaixaDoDia({ quando }: { quando: string }) {
  return (
    <div className="sticky top-1 z-10 flex justify-center py-1">
      <span className="rounded-full bg-neutral-200/90 px-3 py-0.5 text-[11px] font-medium text-neutral-600 backdrop-blur dark:bg-neutral-800/90 dark:text-neutral-300">
        {rotuloDoDia(quando)}
      </span>
    </div>
  );
}

function Bolha({
  mensagem,
  grupo,
  minha,
  lida,
  onResponder,
  onEditar,
  onApagar,
}: {
  mensagem: MensagemCanalChat;
  grupo: boolean;
  minha: boolean;
  lida: boolean;
  onResponder: () => void;
  onEditar?: () => void;
  onApagar?: () => void;
}) {
  /**
   * No celular não há "passar o mouse": as ações abrem num toque longo (ou num toque na
   * bolha) e se fecham no próximo. Deixá-las sempre visíveis encheria a tela de ícones.
   */
  const [acoes, setAcoes] = useState(false);
  // Chamada e aviso não são bolha: são o que aconteceu, e ficam no meio do fio. Sem este
  // desvio cairiam na regra do anexo ausente e diriam "removido pelo prazo de guarda".
  if (mensagem.tipo === 'chamada' || mensagem.tipo === 'aviso') {
    return (
      <p className="mx-auto flex max-w-[90%] items-center gap-1.5 rounded-full bg-neutral-200 px-3 py-1 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        {mensagem.tipo === 'chamada' ? <Phone size={11} /> : <Info size={11} />}
        {mensagem.texto}
        <span className="opacity-60">{carimboDaMensagem(mensagem.criadaEm)}</span>
      </p>
    );
  }
  if (mensagem.removida) {
    return (
      <p className="mx-auto max-w-[90%] rounded bg-neutral-200 px-2 py-1 text-center text-[11px] text-neutral-500 italic dark:bg-neutral-800">
        {mensagem.removidaPeloAutor
          ? 'Mensagem apagada pelo autor'
          : mensagem.removidaPorNome
            ? `Mensagem apagada por ${mensagem.removidaPorNome}`
            : 'Mensagem apagada'}
      </p>
    );
  }
  return (
    <div className={minha ? 'flex flex-col items-end' : 'flex flex-col items-start'}>
      <button
        type="button"
        onClick={() => setAcoes((v) => !v)}
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-left text-sm whitespace-pre-line shadow-sm ${
          minha
            ? 'rounded-br-sm bg-marca-600 text-white'
            : 'rounded-bl-sm bg-white dark:bg-neutral-900'
        }`}
      >
        {/* No grupo, quem falou vem antes do que foi dito; no direto é sempre a mesma dupla. */}
        {grupo && !minha && (
          <p className="mb-0.5 text-[11px] font-medium opacity-70">{mensagem.autor?.nome}</p>
        )}
        {mensagem.respondeA && (
          // Dois níveis, como no WhatsApp: em cima quem escreveu a original, embaixo o que
          // ela dizia. `span`, e não `p`: a bolha inteira é um botão, e parágrafo dentro de
          // botão é HTML inválido.
          <span
            className={`mb-1 block rounded border-l-2 px-2 py-1 text-[11px] opacity-90 ${
              minha ? 'border-white/60 bg-white/10' : 'border-marca-500 bg-black/5 dark:bg-white/10'
            }`}
          >
            <span className="block font-medium">{mensagem.respondeA.autorNome ?? 'Alguém'}</span>
            <span className="block truncate opacity-80">{mensagem.respondeA.resumo}</span>
          </span>
        )}
        {mensagem.midia && (
          <Anexo
            url={mensagem.midia.url}
            nomeOriginal={mensagem.midia.nomeOriginal}
            mimeType={mensagem.midia.mimeType}
            tamanho={mensagem.midia.tamanho}
            previaUrl={mensagem.midia.previaUrl}
            perguntarOndeSalvar
          />
        )}
        {/* O arquivo saiu pelo prazo de guarda e a mensagem ficou. Sem esta linha a bolha
            viraria um retângulo vazio, e quem lesse o fio não entenderia o que houve ali. */}
        {!mensagem.midia && mensagem.tipo !== 'texto' && (
          <p className="text-[11px] italic opacity-70">
            Anexo removido pelo prazo de guarda da conversa interna.
          </p>
        )}
        <TextoComMencoes texto={mensagem.texto} minha={minha} />
        <span className="ml-2 inline-flex items-center gap-1 align-bottom text-[10px] opacity-60">
          {mensagem.editadaEm && <span>editada</span>}
          {carimboDaMensagem(mensagem.criadaEm)}
          {/* O "visto" é só das minhas: saber que a MINHA mensagem foi lida é o que
              importa — o outro já sabe o que leu. */}
          {minha && <CheckCheck size={13} className={lida ? 'opacity-100' : 'opacity-40'} />}
        </span>
      </button>

      {acoes && (
        <div className="mt-1 flex gap-3 px-2 text-[11px] text-neutral-500">
          <button
            type="button"
            onClick={() => {
              setAcoes(false);
              onResponder();
            }}
          >
            Responder
          </button>
          {onEditar && (
            <button
              type="button"
              onClick={() => {
                setAcoes(false);
                onEditar();
              }}
            >
              Editar
            </button>
          )}
          {onApagar && (
            <button
              type="button"
              className="text-red-600 dark:text-red-400"
              onClick={() => {
                setAcoes(false);
                onApagar();
              }}
            >
              Apagar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * O mínimo que a bolinha precisa saber de alguém — serve ao participante do canal e a
 * qualquer outro lugar que mostre presença.
 */
type Presente = {
  online: boolean;
  vistoEm: string | null;
  presenca?: 'online' | 'pausa' | 'offline';
  /** Escolheu não mostrar presença: nem bolinha, nem "visto por último". */
  oculto?: boolean;
  /** O motivo da pausa, que é o status desta pessoa: "Reunião", "Almoço/Janta". */
  pausaAtual?: string | null;
};

/**
 * O rosto de quem está do outro lado, com a bolinha de presença no canto.
 *
 * Verde é "está com o MyMAXX aberto agora", e vale igual para a equipe e para o técnico.
 * O âmbar fica para o atendente em pausa: está aqui, mas longe do cliente.
 *
 * Sem foto, as iniciais — nunca um boneco genérico, que seria igual para todo mundo.
 */
function Retrato({
  pessoa,
}: {
  pessoa?: (Presente & { nome?: string; avatarUrl?: string | null }) | null;
}) {
  const iniciais = (pessoa?.nome ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <span className="relative inline-block shrink-0">
      {pessoa?.avatarUrl ? (
        <img src={pessoa.avatarUrl} alt="" className="size-8 rounded-full object-cover" />
      ) : (
        <span className="flex size-8 items-center justify-center rounded-full bg-neutral-200 text-[11px] font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">
          {iniciais}
        </span>
      )}
      {/* Quem está invisível não ganha bolinha nenhuma. Pintá-la de cinza diria "offline",
        que é uma afirmação sobre a pessoa — e não é o que ela pediu. */}
      {!pessoa?.oculto && (
        <span
          className={`absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-white dark:border-neutral-900 ${
            pessoa?.online
              ? pessoa.presenca === 'pausa'
                ? 'bg-amber-500'
                : 'bg-green-500'
              : 'bg-neutral-300 dark:bg-neutral-600'
          }`}
          aria-hidden="true"
        />
      )}
    </span>
  );
}

/** "Disponível", "Em pausa" ou o visto por último, em uma linha. */
function situacao(pessoa: Presente): string {
  // Invisível não é offline: é SEM status. Dizer "fora do sistema" seria mentir para quem
  // olha, e dizer "visto às 14h" seria entregar justamente o que a pessoa desligou.
  if (pessoa.oculto) return '';
  // O motivo da pausa é o status: "Em reunião" diz muito mais do que "Em pausa", e é a
  // diferença entre insistir e esperar.
  if (pessoa.online && pessoa.pausaAtual) return `Em ${pessoa.pausaAtual.toLowerCase()}`;
  if (pessoa.online) return pessoa.presenca === 'pausa' ? 'Em pausa' : 'Disponível';
  if (!pessoa.vistoEm) return 'Nunca entrou';
  return `Visto ${desdeQuando(pessoa.vistoEm)}`;
}

/**
 * "agora há pouco", "há 12 min", "há 3 h", "ontem às 18:30", "em 02/10 às 09:14".
 *
 * Quanto mais velho, menos precisão — ninguém precisa saber que alguém esteve aqui há 437
 * minutos, e "há 7 h" é o que a pessoa realmente quer saber.
 */
function desdeQuando(iso: string): string {
  const quando = new Date(iso);
  const minutos = Math.floor((Date.now() - quando.getTime()) / 60_000);
  if (minutos < 2) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} min`;

  const hoje = new Date();
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (quando.toDateString() === hoje.toDateString()) return `há ${Math.floor(minutos / 60)} h`;

  const ontem = new Date(hoje.getTime() - 86_400_000);
  if (quando.toDateString() === ontem.toDateString()) return `ontem às ${hora}`;
  return `em ${quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${hora}`;
}

/**
 * A minha mensagem já foi lida por todo mundo que está no canal?
 *
 * Em grupo o critério é "todos": dois tiques acesos com metade lida diriam o que não é.
 */
function lidaPorTodos(
  mensagem: MensagemCanalChat,
  membros: ParticipanteCanal[],
  meuId: string | undefined,
): boolean {
  const outros = membros.filter((m) => !m.saiu && !(m.tipo === 'tecnico' && m.id === meuId));
  if (outros.length === 0) return false;
  const quando = new Date(mensagem.criadaEm).getTime();
  return outros.every((m) => !!m.lidoAte && new Date(m.lidoAte).getTime() >= quando);
}

/**
 * O texto da bolha, com os nomes chamados em destaque.
 *
 * Destaca todo "@palavra", e não só quem existe no canal: quem decide o aviso é o
 * servidor, e conferir aqui daria uma segunda verdade sobre quem foi chamado. O destaque é
 * leitura, não regra.
 */
function TextoComMencoes({ texto, minha }: { texto: string | null; minha: boolean }) {
  if (!texto) return null;
  const partes = texto.split(/(@[\p{L}\p{N}._-]{2,40})/gu);
  return (
    <>
      {partes.map((parte, i) =>
        parte.startsWith('@') ? (
          <span
            key={i}
            className={`rounded px-0.5 font-medium ${
              minha
                ? 'bg-white/25'
                : 'bg-marca-100 text-marca-800 dark:bg-marca-900 dark:text-marca-100'
            }`}
          >
            {parte}
          </span>
        ) : (
          <span key={i}>{parte}</span>
        ),
      )}
    </>
  );
}
